import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon, message, Modal, Tooltip, Upload } from 'antd';
import appSettings from '../../../../constants/app.constants';
import { authUploadHeader } from '../../../../helpers/auth-header';
import { useCreateCityPortalFilesMutation } from '../../mutations/useCreateCityPortalFilesMutation';
import { useDispatch } from 'react-redux';
import { requestsActions } from '../../../Requests/requests.actions';
import showErrorMessage from '../../../../helpers/showErrorMessage';

const translatedErrors = {
  'Address not found': 'Адрес не найден',
  'Request type not found': 'Причина не найдена',
};

export const ImportFromCityPortal = ({ visible, onCancel, onSuccess }) => {
  const dispatch = useDispatch();

  const [fileList, setFileList] = useState([]);
  const beforeUpload = useCallback((file, fileList, showErrors = true) => {
    // if (file.size > 15 * 1024 * 1024) {} // e.g. 15 MB
    if (
      file.type !==
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      showErrors && message.error(`Файл должен быть docx (${file.name})`);
      return false;
    }
    return true;
  }, []);

  const onRemove = useCallback((fileWrapper) => {
    return new Promise((resolve, reject) => {
      Modal.confirm({
        title: 'Удалить файл?',
        content: <div>Действительно удалить файл "{fileWrapper.name}"?</div>,
        okText: 'Удалить',
        cancelText: 'Отмена',
        onOk: () => {
          resolve();
        },
        onCancel: () => {
          reject('Deletion cancelled.');
        },
      });
    });
  }, []);

  const onChange = ({ event, file, fileList }) => {
    setFileList(
      fileList
        .filter((file) => {
          return beforeUpload(file, fileList, false);
        })
        .map((fw) => {
          if (fw.status === 'error') {
            const text = translatedErrors[fw.response];
            if (text) {
              fw.response = text;
            }
          }
          return fw;
        })
    );
  };

  const fileListInfo = useMemo(() => {
    // uploading, done, error, removed
    const hasUploading = fileList.some(
      (fileWrapper) => fileWrapper.status === 'uploading'
    );
    const hasError = fileList.some(
      (fileWrapper) => fileWrapper.status === 'error'
    );
    const hasDone = fileList.some(
      (fileWrapper) => fileWrapper.status === 'done'
    );

    return {
      hasUploading,
      hasError,
      hasDone,
    };
  }, [fileList]);

  const beforeModalClose = useCallback(() => {
    if (!fileList.length) {
      onCancel();
      return;
    }

    Modal.confirm({
      title: 'Закрыть окно?',
      content: (
        <div>
          Действительно закрыть окно загрузки? Ваши несохраненные данные будут
          потеряны.
        </div>
      ),
      okText: 'Закрыть',
      cancelText: 'Отмена',
      onOk: () => {
        onCancel();
      },
      onCancel: () => {},
    });
  }, [fileList.length, onCancel]);

  const createCityPortalFilesMutation = useCreateCityPortalFilesMutation({
    onSuccess: (response, mutationArgs) => {
      const isLengthEqual = response.data.length === mutationArgs.files.length;
      const allAreOk = response.data.every((el) => el.isOk);
      const createdRequestIds = response.data
        .filter((el) => el.isOk)
        .map((el) => el.requestId);
      const errorFiles = response.data
        .filter((el) => !el.isOk)
        .map((el) => {
          const fileWrapper = fileList.find(
            (fileWrapper) => fileWrapper.name === el.fileName
          );
          const fileUrl = fileWrapper?.originFileObj
            ? URL.createObjectURL(fileWrapper.originFileObj)
            : '';
          return {
            ...el,
            fileWrapper,
            fileUrl,
          };
        });

      dispatch(requestsActions.fetchImportResultRequests(createdRequestIds));
      dispatch(
        requestsActions.setImportResult({
          isLengthEqual,
          allAreOk,
          createdRequestIds,
          errorFiles,
        })
      );

      onSuccess();
    },
    onError: (error, variables, context) => {
      showErrorMessage(error, 'Произошла ошибка импорта');
      console.log('Error (ImportFromCityPortal): ', {
        error,
        variables,
        context,
      });
    },
  });

  const disabled =
    !fileList.length || fileListInfo.hasUploading || !fileListInfo.hasDone;

  const handleOk = async () => {
    if (disabled) {
      return;
    }

    if (fileListInfo.hasError) {
      const continueConfirmed = await new Promise((resolve, reject) => {
        Modal.confirm({
          title: 'Не все файлы будут загружены',
          content: (
            <div>
              Некоторые файлы содержат ошибки и не будут загружены. Вы уверены
              что хотите загрузить только файлы без ошибок?
            </div>
          ),
          okText: 'Загрузить',
          cancelText: 'Отмена',
          onOk: () => {
            resolve(true);
          },
          onCancel: () => {
            resolve(false);
          },
        });
      });

      if (!continueConfirmed) {
        return;
      }
    }

    const fileNames = fileList
      .filter((fileWrapper) => fileWrapper.status === 'done')
      .map((fileWrapper) => fileWrapper.name);

    const uniqueFileNames = Array.from(new Set(fileNames));

    createCityPortalFilesMutation.mutate({ files: uniqueFileNames });
  };

  const headers = useMemo(authUploadHeader, []);

  return (
    <Modal
      visible={visible}
      title="Загрузить заявки с городского портала"
      onCancel={beforeModalClose}
      okText="Загрузить"
      okButtonProps={{
        disabled,
      }}
      onOk={handleOk}
      afterClose={() => {
        setFileList([]);
      }}
    >
      <div>Загрузите все заявки в расширении .docx</div>
      <div>
        <Upload.Dragger
          name="file"
          multiple
          accept=".docx"
          action={appSettings.apiUrl + '/Request/city_portal_file'}
          headers={headers}
          fileList={fileList}
          onChange={onChange}
          beforeUpload={beforeUpload}
          showUploadList={{ showRemoveIcon: true }}
          onRemove={onRemove}
        >
          <Tooltip title="Чтобы прикрепить файл, перетащите его сюда, или нажмите чтобы выбрать">
            <Icon
              type="file-add"
              theme="outlined"
            />
          </Tooltip>
        </Upload.Dragger>
      </div>
    </Modal>
  );
};

ImportFromCityPortal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
